import React, { useState } from "react";
import { Star } from "lucide-react";

/**
 * Renders the cached Google Places reviews for a park.
 *
 * Data shape (from the backend, populated when /api/places/{id} hits a
 * cached doc that includes the Atmosphere SKU field `reviews`):
 *   - author_name, rating (0-5), text, relative_time_description, time
 *
 * If `reviews` is missing or empty, this component renders nothing so
 * the parent doesn't have to guard.
 */

// Brand stars in primary forest green per spec. Empty stars use the
// warm border color so the unfilled state still reads as a "rating".
const RatingStars = ({ value }) => (
  <div
    className="flex items-center gap-0.5"
    aria-label={`Rated ${value} of 5`}
    data-testid="google-review-stars"
  >
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`w-3.5 h-3.5 ${
          n <= value
            ? "fill-[#4A7C59] text-[#4A7C59]"
            : "text-brand-border-warm"
        }`}
      />
    ))}
  </div>
);

// Inline Google "G" mark. Kept as raw SVG so we don't add an icon dep
// and so the colors render correctly on the cream card background.
const GoogleG = ({ className = "w-4 h-4" }) => (
  <svg
    viewBox="0 0 48 48"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="#FFC107"
      d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
    />
    <path
      fill="#FF3D00"
      d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.2 0 9.8-1.9 13.2-5.1l-6.1-5c-2 1.4-4.5 2.1-7.1 2.1-5.3 0-9.8-3.3-11.3-8l-6.5 5C9.4 39.6 16.1 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.6 20.5H42V20H24v8h11.3c-.7 2.2-2.1 4.1-3.9 5.3l6.1 5C40.4 35.9 44 30.4 44 24c0-1.3-.1-2.4-.4-3.5z"
    />
  </svg>
);

// One review card. Long text collapses to ~3 lines via line-clamp
// (CSS class on App.css below) with a "Read more" toggle that
// expands inline — no modals, no layout shift.
const ReviewCard = ({ review, testId }) => {
  const [expanded, setExpanded] = useState(false);
  const text = review.text || "";
  // ~210 chars roughly fills 3 lines at our body type size; the actual
  // clamp is CSS, this just decides whether to render the toggle.
  const isLong = text.length > 220;
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border p-4 sm:p-5"
      style={{ backgroundColor: "#F5F0E8", borderColor: "#D4C9B0" }}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="font-body font-semibold text-sm" style={{ color: "#1A1A0F" }}>
          {review.author_name || "Google reviewer"}
        </div>
        <RatingStars value={review.rating || 0} />
      </div>
      <p
        className={`font-body text-sm mt-2 leading-relaxed whitespace-pre-line ${
          !expanded && isLong ? "line-clamp-3" : ""
        }`}
        style={{ color: "#1A1A0F" }}
        data-testid="google-review-text"
      >
        {text}
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="google-review-toggle"
          className="mt-1.5 text-xs font-semibold transition-opacity hover:opacity-80"
          style={{ color: "#4A7C59" }}
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
      {review.relative_time_description && (
        <div
          className="text-xs mt-2"
          style={{ color: "#1A1A0F", opacity: 0.55 }}
        >
          {review.relative_time_description}
        </div>
      )}
    </div>
  );
};

const GoogleReviewsSection = ({ reviews }) => {
  if (!Array.isArray(reviews) || reviews.length === 0) return null;

  // Sort by most-recent first. `time` is unix seconds from Google;
  // missing/zero values sink to the bottom rather than the top.
  const sorted = [...reviews]
    .filter((r) => r && (r.text || "").trim())
    .sort((a, b) => (b.time || 0) - (a.time || 0))
    .slice(0, 5);

  if (sorted.length === 0) return null;

  return (
    <section
      className="mt-10"
      data-testid="google-reviews-section"
      aria-labelledby="google-reviews-heading"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <h2
          id="google-reviews-heading"
          className="font-heading text-2xl font-bold text-brand-ink"
        >
          Google Reviews
        </h2>
        <span className="inline-flex items-center gap-1.5 text-xs text-brand-ink/55">
          <GoogleG className="w-4 h-4" />
          <span>(from Google)</span>
        </span>
      </div>
      <div className="mt-5 space-y-3" data-testid="google-reviews-list">
        {sorted.map((r, i) => (
          <ReviewCard
            key={`${r.author_name || "anon"}-${r.time || i}`}
            review={r}
            testId={`google-review-${i}`}
          />
        ))}
      </div>
    </section>
  );
};

export default GoogleReviewsSection;
